import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./forgotPassword.css";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";

const generatePassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = '!@#$%^&*(),.?":{}|<>';
    const all = upper + lower + numbers + special;
    const pick = (str) => str[crypto.getRandomValues(new Uint8Array(1))[0] % str.length];
    const chars = [pick(upper), pick(lower), pick(numbers), pick(special), ...Array.from({ length: 12 }, () => pick(all))];
    for (let i = chars.length - 1; i > 0; i--) {
        const j = crypto.getRandomValues(new Uint8Array(1))[0] % (i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
};

const ForgotPassword = ({ setShowForgotPassword }) => {
    const toast = useToast();
    const [email, setEmail] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [stage, setStage] = useState(1);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    const handleGeneratePassword = () => {
        const generated = generatePassword();
        setNewPassword(generated);
        setConfirmPassword(generated);
        setShowPassword(true);
        validatePassword(generated);
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const validatePassword = (password) => {
        setPasswordRequirements({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        });
    };

    const handlePasswordChange = async (event) => {
        event.preventDefault();
        try {
            await apiClient.post(`/user/reset-password`, {
                username: email,
                verificationCode,
                newPassword,
            });
            setShowForgotPassword(false);
            toast.success("Password reset successfully!");
        } catch (error) {
            console.log(error);
            toast.error("Invalid verification code. Please try again.");
        }
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        await apiClient.post(`/user/forgot-password`, {
            username: email,
        });
        setStage(2);
    };

    return (
        <div className="forgot-password-container">
            <div className="forgot-password-form">
                <h3>Forgot Password</h3>
                <form>
                    {stage === 1 && (
                        <>
                            <p>Please enter your email address to reset your password.</p>
                            <input
                                onChange={handleEmailChange}
                                type="email"
                                placeholder="Email"
                                name="username"
                                autoComplete="username"
                                required
                            />
                            <button
                                onClick={(e) => {
                                    handleResetPassword(e);
                                }}
                            >
                                Send Reset Link
                            </button>
                        </>
                    )}
                    {stage === 2 && (
                        <>
                            <p>A verification code has been sent to your email</p>
                            <input
                                type="text"
                                placeholder="Verification Code"
                                required
                                name="verification-code"
                                autoComplete="off"
                                onChange={(e) => setVerificationCode(e.target.value)}
                            />
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                name="new-password"
                                autoComplete="new-password"
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    validatePassword(e.target.value);
                                }}
                                required
                            />
                            <button type="button" className="generate-password-btn" onClick={handleGeneratePassword}>
                                Generate Password
                            </button>
                            <div className="password-requirements">
                                <div className={`requirement${passwordRequirements.length ? " met" : ""}`}>
                                    <span className="checkmark">{passwordRequirements.length ? "✓" : "○"}</span>
                                    <span>At least 8 characters</span>
                                </div>
                                <div className={`requirement${passwordRequirements.uppercase ? " met" : ""}`}>
                                    <span className="checkmark">{passwordRequirements.uppercase ? "✓" : "○"}</span>
                                    <span>One uppercase letter</span>
                                </div>
                                <div className={`requirement${passwordRequirements.lowercase ? " met" : ""}`}>
                                    <span className="checkmark">{passwordRequirements.lowercase ? "✓" : "○"}</span>
                                    <span>One lowercase letter</span>
                                </div>
                                <div className={`requirement${passwordRequirements.number ? " met" : ""}`}>
                                    <span className="checkmark">{passwordRequirements.number ? "✓" : "○"}</span>
                                    <span>One number</span>
                                </div>
                                <div className={`requirement${passwordRequirements.special ? " met" : ""}`}>
                                    <span className="checkmark">{passwordRequirements.special ? "✓" : "○"}</span>
                                    <span>One special character</span>
                                </div>
                            </div>
                            <div className="show-password-toggle">
                                <input
                                    type="checkbox"
                                    id="forgotShowPassword"
                                    checked={showPassword}
                                    onChange={() => setShowPassword(!showPassword)}
                                />
                                <label htmlFor="forgotShowPassword">Show Password</label>
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                name="confirm-password"
                                autoComplete="new-password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (newPassword !== confirmPassword) {
                                        toast.error("Passwords do not match");
                                        return;
                                    }
                                    if (Object.values(passwordRequirements).every((req) => req)) {
                                        handlePasswordChange(e);
                                    } else {
                                        toast.error("Please meet all password requirements");
                                    }
                                }}
                            >
                                Reset Password
                            </button>
                        </>
                    )}
                </form>
                <p
                    className="back-to-login"
                    onClick={() => {
                        setStage(1);
                        setShowForgotPassword(false);
                    }}
                >
                    Back to Login
                </p>
            </div>
        </div>
    );
};

export default ForgotPassword;
